import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import StudioLayout from "@/components/layouts/StudioLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function StudioBilling() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/tenant/me")
      .then((r) => setTenant(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  const update = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/tenant/me", patch);
      setTenant(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !tenant) {
    return <StudioLayout tenant={tenant}><div className="text-white/40 text-sm uppercase tracking-widest">Loading…</div></StudioLayout>;
  }

  return (
    <StudioLayout tenant={tenant}>
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Studio admin · Billing</p>
        <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl tracking-tight">Payments &amp; billing</h1>
      </div>

      <div className="border border-amber-500/40 bg-amber-500/10 p-5 mb-6 flex items-start gap-3" data-testid="studio-billing-mock-banner">
        <AlertTriangle className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-display font-bold text-amber-200">Payments are running in MOCK mode</div>
          <p className="text-sm text-amber-200/70 font-light mt-1">
            Real Stripe / Razorpay SDKs are not wired yet. All checkouts complete via a mock confirmation page so you can demo the flow end-to-end. When you connect a provider, only the server-side <code className="font-mono text-amber-100">PaymentProvider</code> seam needs to change.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-[#121212] border border-white/10 p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Your tenant plan</div>
          <div className="flex items-center gap-3">
            <Badge className="rounded-none uppercase text-[11px] tracking-[0.2em] bg-emerald-500/15 text-emerald-300">{tenant.plan}</Badge>
            <span className="text-sm text-white/50">Status · {tenant.status}</span>
          </div>
          <p className="mt-4 text-sm text-white/60 font-light">
            Plan upgrades and downgrades are managed by Vawworld superadmins. Reach out to your account contact to change tier.
          </p>
          <div className="mt-6 pt-4 border-t border-white/10 text-xs text-white/40 flex items-center justify-between">
            <span>White label</span>
            <Badge className={`rounded-none uppercase text-[10px] tracking-[0.2em] ${tenant.white_label_enabled ? "bg-amber-500/15 text-amber-300" : "bg-white/10 text-white/50"}`}>
              {tenant.white_label_enabled ? "enabled" : "powered by Vawworld"}
            </Badge>
          </div>
        </div>

        <div className="bg-[#121212] border border-white/10 p-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Provider preference</div>
          <div className="space-y-3">
            <Field label="Default checkout provider">
              <Select value={tenant.payment_provider_preference} onValueChange={(v) => update({ payment_provider_preference: v })}>
                <SelectTrigger className="bg-transparent border-white/15 rounded-none" data-testid="studio-billing-provider"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 rounded-none text-white">
                  <SelectItem value="razorpay">Razorpay</SelectItem>
                  <SelectItem value="stripe">Stripe</SelectItem>
                  <SelectItem value="mock">Mock (testing)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Default currency">
              <Select value={tenant.default_currency} onValueChange={(v) => update({ default_currency: v })}>
                <SelectTrigger className="bg-transparent border-white/15 rounded-none" data-testid="studio-billing-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 rounded-none text-white">
                  <SelectItem value="INR">INR · ₹</SelectItem>
                  <SelectItem value="USD">USD · $</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {saving && <div className="text-xs text-white/50 inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</div>}
          </div>
        </div>
      </div>
    </StudioLayout>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.3em] text-white/50">{label}</Label>
      {children}
    </div>
  );
}
